
import { cn } from "@/lib/utils";
import { CardTitle } from "@/components/ui/card";
import { BadgePercent } from "lucide-react";
import { Link } from "react-router-dom";

type LogoProps = {
  className?: string;
  size?: "sm" | "md" | "lg";
  showIcon?: boolean;
  clickable?: boolean;
  stacked?: boolean; // New prop to control stacked layout
};

export function Logo({ className, size = "md", showIcon = true, clickable = false, stacked = false }: LogoProps) {
  const sizeClasses = {
    sm: "text-xl",
    md: "text-2xl", // Reduced from text-3xl for better sidebar fit
    lg: "text-4xl",
  };
  
  const iconSizes = {
    sm: 20, // Increased from 18
    md: 28, // Increased from 24
    lg: 36, // Increased from 32
  };
  
  // For compact version, show "CB" instead of full name
  const logoText = size === "sm" ? "CB" : stacked ? null : "Conversion Bonus";
  
  const logoContent = (
    <>
      {showIcon && (
        <BadgePercent 
          size={iconSizes[size]} 
          className="text-brand-teal" 
          strokeWidth={2.5}
        />
      )}
      {stacked ? (
        <div className="flex flex-col leading-none">
          <CardTitle 
            className={cn(
              `font-bold bg-gradient-to-r from-brand-teal to-brand-coral bg-clip-text text-transparent ${sizeClasses[size]}`
            )}
          >
            Conversion
          </CardTitle>
          <CardTitle 
            className={cn(
              `font-bold bg-gradient-to-r from-brand-teal to-brand-coral bg-clip-text text-transparent ${sizeClasses[size]} -mt-1`
            )}
          >
            Bonus
          </CardTitle>
        </div>
      ) : (
        <CardTitle 
          className={cn(
            `font-bold bg-gradient-to-r from-brand-teal to-brand-coral bg-clip-text text-transparent tracking-tight ${sizeClasses[size]}`
          )}
        >
          {logoText}
        </CardTitle>
      )}
    </>
  );

  if (clickable) {
    return (
      <Link 
        to="/dashboard" 
        className={cn(
          "flex items-center gap-1.5", // Reduced gap from gap-2 to tighten spacing
          className
        )}
        aria-label="Go to dashboard"
      >
        {logoContent}
      </Link>
    );
  }
  
  return (
    <div className={cn("flex items-center gap-1.5", className)}> {/* Reduced gap from gap-2 */}
      {logoContent}
    </div>
  );
}
